// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	BADGE_ORDER_MAX,
	BadgeOrderKeySchema,
	BoostProfileBadgeKeySchema,
	CatalogBadgeIdSchema,
	FlagBadgeKeySchema,
	PremiumProfileBadgeKeySchema,
} from '@fluxer/schema/src/domains/badge/BadgeVisibilitySchemas';
import {SnowflakeStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

export const BadgeResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the badge'),
	icon: z.string().describe('Hash of the badge icon'),
	description: z.string().describe('The text shown for the badge'),
});

export type BadgeResponse = z.infer<typeof BadgeResponse>;

export const BadgeListResponse = z.array(BadgeResponse).describe('Every badge in the catalog');

export type BadgeListResponse = z.infer<typeof BadgeListResponse>;

const TierIconHash = z
	.string()
	.regex(/^(a_)?[0-9a-f]{8}$/)
	.nullable();

export const BoosterBadgeIconsResponse = z
	.array(TierIconHash)
	.length(9)
	.describe('Uploaded icon hash of booster badge tiers 1 to 9; null keeps the bundled image');

export type BoosterBadgeIconsResponse = z.infer<typeof BoosterBadgeIconsResponse>;

const TierName = z.string().min(1).max(32);

export const StaffBadgeTierResponse = z.object({
	tr: TierName.describe('Turkish tier name'),
	en: TierName.describe('English tier name'),
	icon: TierIconHash.describe('Uploaded icon hash; null keeps the bundled image'),
});

export const StaffBadgeTiersResponse = z
	.array(StaffBadgeTierResponse)
	.length(12)
	.describe('Staff badge tiers 1 to 12; an admin gives each staff account one tier');

export type StaffBadgeTiersResponse = z.infer<typeof StaffBadgeTiersResponse>;

export const BotDeveloperBadgeTiersResponse = z
	.array(
		StaffBadgeTierResponse.extend({
			threshold: z
				.number()
				.int()
				.min(0)
				.max(10_000)
				.describe('Distinct server count the tier starts at, at most 10000'),
		}),
	)
	.length(7)
	.refine(
		(tiers) => tiers.every((tier, i) => (i === 0 ? tier.threshold === 1 : tier.threshold > tiers[i - 1]!.threshold)),
		'Tier 1 starts at 1 and every next threshold is higher',
	)
	.describe("Bot developer badge tiers 1 to 7 by distinct servers of the owner's verified bots");

export type BotDeveloperBadgeTiersResponse = z.infer<typeof BotDeveloperBadgeTiersResponse>;

export const BotDeveloperMinMembers = z
	.number()
	.int()
	.min(1)
	.max(10000)
	.describe('Members a server needs to count toward the bot developer badge');

export const BotDeveloperBadgeConfigResponse = z.object({
	tiers: BotDeveloperBadgeTiersResponse,
	min_members: BotDeveloperMinMembers,
});

export type BotDeveloperBadgeConfigResponse = z.infer<typeof BotDeveloperBadgeConfigResponse>;

export const BadgeInventoryItemResponse = z.discriminatedUnion('source', [
	z.object({
		source: z.literal('flag'),
		key: FlagBadgeKeySchema.exclude(['premium', 'premium_package']),
		visible: z.boolean(),
		tier: z.number().int().min(1).max(12).optional().describe('Staff badge tier; only on staff with a tier'),
	}),
	z.object({
		source: z.literal('premium'),
		key: z.union([FlagBadgeKeySchema.extract(['premium', 'premium_package']), PremiumProfileBadgeKeySchema]),
		visible: z.boolean(),
		months: z
			.union([z.literal(0), z.literal(1), z.literal(3), z.literal(12)])
			.optional()
			.describe('Tier of the paid package badge in months, 0 the single badge; only on premium_package'),
	}),
	z.object({
		source: z.literal('boost'),
		key: BoostProfileBadgeKeySchema.extract(['booster']),
		visible: z.boolean(),
		since: z.string().describe('ISO8601 start of the unbroken run of boosting the badge counts from'),
	}),
	z.object({
		source: z.literal('bot_developer'),
		key: BoostProfileBadgeKeySchema.extract(['bot_developer']),
		visible: z.boolean(),
		guilds: z.number().int().min(0).describe("Distinct qualifying servers of the owner's verified bots"),
	}),
]);

export type BadgeInventoryItemResponse = z.infer<typeof BadgeInventoryItemResponse>;

export const CatalogBadgeInventoryItemResponse = z.object({
	source: z.literal('catalog'),
	badge_id: CatalogBadgeIdSchema,
	visible: z.boolean(),
	icon: BadgeResponse.shape.icon,
	description: BadgeResponse.shape.description,
});

export type CatalogBadgeInventoryItemResponse = z.infer<typeof CatalogBadgeInventoryItemResponse>;

export const BadgeInventoryResponse = z.object({
	badges: z.array(BadgeInventoryItemResponse).max(8).describe('All owned built-in badges, including hidden ones'),
	catalog: z
		.array(CatalogBadgeInventoryItemResponse)
		.max(100)
		.describe('A page of owned catalog badges, including hidden ones'),
	next_cursor: CatalogBadgeIdSchema.nullable().describe(
		'Last catalog badge ID when another page exists; null at the end',
	),
	order: z
		.array(BadgeOrderKeySchema)
		.max(BADGE_ORDER_MAX)
		.describe('Saved badge order, hidden badges included; empty keeps the default order'),
});

export type BadgeInventoryResponse = z.infer<typeof BadgeInventoryResponse>;
